import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const input = this.refs.quantityInput;
    const step = parseInt(input.step) || 1;
    const current = parseInt(input.value) || 0;
    const newValue = current + step;

    input.value = newValue;
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const input = this.refs.quantityInput;
    const step = parseInt(input.step) || 1;
    const current = parseInt(input.value) || 0;
    const min = parseInt(input.min) || step;
    const newValue = Math.max(current - step, min);

    input.value = newValue;
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;
    quantityInput.select();
  }

  /**
   * Handles manual quantity entry.
   * Forces the value to be a valid multiple of the step/MOQ.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const input = this.refs.quantityInput;
    const step = parseInt(input.step) || 1;
    const min = parseInt(input.min) || step;
    let value = parseInt(input.value) || min;

    // Snap to nearest valid multiple of step
    if (value % step !== 0) {
      value = Math.round(value / step) * step;
      if (value < min) value = min;
    }

    input.value = value;
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks that quantity is within min/max bounds
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const min = parseInt(quantityInput.min) || 1;
    const max = parseInt(quantityInput.max) || Infinity;
    const value = parseInt(quantityInput.value) || min;

    if (value < min) quantityInput.value = min;
    if (value > max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }
    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
